import {
  NetworkStatus,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from "@apollo/client";
import {
  Button,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  PrimaryFullScreenSpinner,
  Tabs,
  TabsList,
  TabsTrigger,
  toast,
  useAppForm,
} from "@repo/ui";
import {
  getStartOfTimezoneDay,
  NotFoundError,
  neverFunc,
  prettyPrintEnum,
  REPORT_TIMEZONE,
  useUserTimezone,
} from "@repo/utils";
import { useStore } from "@tanstack/react-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { Suspense, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type {
  AdminStatementBatchRowFragment,
  StatementKind,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/statement-batches/$kind")({
  head: () => opsHead("Statements"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function getColumns(
  kind: StatementKind,
): ColumnDef<AdminStatementBatchRowFragment & { id: string }>[] {
  const batchColumn: ColumnDef<
    AdminStatementBatchRowFragment & { id: string }
  > = {
    id: "id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Batch" />
    ),
    accessorFn: (row) => `#${row.shortRef}`,
    enableSorting: false,
  };

  const periodColumn: ColumnDef<
    AdminStatementBatchRowFragment & { id: string }
  > = {
    id: "period",
    accessorFn: (row) => row.periodEndAtExclusive,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={kind === "BALANCE" ? "Date" : "Period"}
      />
    ),
    cell: ({ row }) => {
      if (kind === "BALANCE") {
        return row.original.periodEndAtExclusive ? (
          <DateUI date={row.original.periodEndAtExclusive} />
        ) : (
          <div>N/A</div>
        );
      }
      const startAtInclusive = row.original.periodStartAtInclusive;
      const endAtExclusive = row.original.periodEndAtExclusive;
      return startAtInclusive != null && endAtExclusive != null ? (
        <div className="flex flex-row gap-1">
          <DateUI date={startAtInclusive} />
          <span>&nbsp;-&nbsp;</span>
          <DateUI date={endAtExclusive} />
        </div>
      ) : (
        <div>N/A</div>
      );
    },
    enableSorting: false,
  };

  const generatedCountColumn: ColumnDef<
    AdminStatementBatchRowFragment & { id: string }
  > = {
    id: "generatedCount",
    accessorFn: (row) => row.statements?.length ?? 0,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="# in batch" />
    ),
    enableSorting: false,
  };

  const notPublishedCountColumn: ColumnDef<
    AdminStatementBatchRowFragment & { id: string }
  > = {
    id: "notPublishedCount",
    accessorFn: (row) =>
      (row.statements ?? []).filter(
        (statement) => statement.status !== "PUBLISHED",
      ).length,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="# not published" />
    ),
    enableSorting: false,
  };

  const publishedCountColumn: ColumnDef<
    AdminStatementBatchRowFragment & { id: string }
  > = {
    id: "publishedCount",
    accessorFn: (row) =>
      (row.statements ?? []).filter(
        (statement) => statement.status === "PUBLISHED",
      ).length,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="# published" />
    ),
    enableSorting: false,
  };

  const createdAtColumn: ColumnDef<
    AdminStatementBatchRowFragment & { id: string }
  > = {
    id: "createdAt",
    accessorFn: (row) => row.createdAt,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created at" />
    ),
    cell: ({ row }) => (
      <DateUI date={row.original.createdAt} variant="DATETIME" />
    ),
    enableSorting: false,
  };

  const totalExGstFeeColumn: ColumnDef<
    AdminStatementBatchRowFragment & { id: string }
  > = {
    id: "totalExGstFee",
    enableSorting: false,
    accessorFn: (row) => row.totalFeeExGst,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Total fees (ex GST)" />
    ),
    cell: ({ row }) => <CurrencyAmountUI ca={row.original.totalFeeExGst} />,
  };

  switch (kind) {
    case "MONTHLY":
      return [
        batchColumn,
        periodColumn,
        generatedCountColumn,
        notPublishedCountColumn,
        publishedCountColumn,
        totalExGstFeeColumn,
        createdAtColumn,
      ];
    case "ACTIVITY":
    case "ACCOUNT":
      return [
        batchColumn,
        periodColumn,
        generatedCountColumn,
        notPublishedCountColumn,
        publishedCountColumn,
      ];
    case "BALANCE":
      return [
        batchColumn,
        generatedCountColumn,
        notPublishedCountColumn,
        publishedCountColumn,
      ];
    default:
      throw neverFunc(kind);
  }
}

const orgSelectionSchema = z
  .array(z.object({ value: z.string(), label: z.string() }))
  .min(
    1,
    "Select at least 1 organisation, or tick the checkbox to generate statements for all organisations",
  )
  .optional();

const periodicFormSchema = z
  .object({
    startAtInclusive: z.date({ error: "Start at is required" }),
    endAtExclusive: z.date({ error: "End at is required" }),
    selectedOrganisationOptions: orgSelectionSchema,
  })
  .superRefine(({ startAtInclusive, endAtExclusive }, ctx) => {
    if (startAtInclusive <= endAtExclusive) {
      return;
    }

    ctx.addIssue({
      code: "custom",
      message: "Start at must be on or before End at",
      path: ["startAtInclusive"],
    });
    ctx.addIssue({
      code: "custom",
      message: "End at must be on or after Start at",
      path: ["endAtExclusive"],
    });
  });

type PeriodicFormInput = z.infer<typeof periodicFormSchema>;

const balanceFormSchema = z.object({
  atExclusive: z.date({ error: "Date is required" }),
  selectedOrganisationOptions: orgSelectionSchema,
});

type BalanceFormInput = z.infer<typeof balanceFormSchema>;

function GenerateStatementBatchDialog(props: {
  kind: StatementKind;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisations: { id: string; name: string | null }[];
}) {
  const { kind, open, onOpenChange, organisations } = props;
  const navigate = useNavigate();
  const userTimezone = useUserTimezone();
  const browserDateTimeFormat = "yyyy-MM-dd'T'HH:mm";

  const organisationOptions = useMemo(
    () =>
      organisations
        .filter(
          (organisation): organisation is { id: string; name: string } =>
            organisation.name !== null,
        )
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(({ id, name }) => ({ value: id, label: name })),
    [organisations],
  );

  const [createStatementBatchMutation] = useMutation(
    graphql(`
      mutation CreateStatementBatch($input: CreateStatementBatchInput!) {
        createStatementBatch(input: $input) {
          statementBatch {
            id
          }
        }
      }
    `),
  );

  const defaultErrorMessage =
    "There was an error submitting your form. Please try again later.";

  const periodicDefaults = useMemo(() => {
    const now = DateTime.now();
    const endAtExclusive = now.setZone(REPORT_TIMEZONE).startOf("month");
    const startAtInclusive = endAtExclusive.minus({ months: 1 });
    return {
      startAtInclusive: startAtInclusive.toJSDate(),
      endAtExclusive: endAtExclusive.toJSDate(),
    };
  }, []);

  const balanceDefaults = useMemo(() => {
    const now = new Date();
    return {
      atExclusive: getStartOfTimezoneDay({
        at: now,
        timezone: REPORT_TIMEZONE,
      }),
    };
  }, []);

  const periodicForm = useAppForm({
    defaultValues: periodicDefaults as PeriodicFormInput,
    validators: {
      onChange: periodicFormSchema,
      onSubmit: periodicFormSchema,
    },
    onSubmit: async ({ value }) => {
      await createStatementBatchMutation({
        variables: {
          input: {
            kind,
            periodStartAtInclusive: value.startAtInclusive,
            periodEndAtExclusive: value.endAtExclusive,
            organisationIds: value.selectedOrganisationOptions?.map(
              ({ value }) => value,
            ),
          },
        },
        onCompleted: (data) => {
          onOpenChange(false);
          void navigate({
            to: "/statement-batches/$kind/$statementBatchId",
            params: {
              kind: kind.toLowerCase(),
              statementBatchId: data.createStatementBatch.statementBatch.id,
            },
          });
        },
        onError: (error) => {
          const description =
            error.graphQLErrors?.[0]?.message ?? defaultErrorMessage;
          toast({
            kind: "error",
            autoHide: false,
            title: "Failed to generate statements",
            description,
          });
          throw error;
        },
      });
    },
  });

  const balanceForm = useAppForm({
    defaultValues: balanceDefaults as BalanceFormInput,
    validators: {
      onSubmit: balanceFormSchema,
    },
    onSubmit: async ({ value }) => {
      await createStatementBatchMutation({
        variables: {
          input: {
            kind,
            periodEndAtExclusive: value.atExclusive,
            organisationIds: value.selectedOrganisationOptions?.map(
              ({ value }) => value,
            ),
          },
        },
        onCompleted: (data) => {
          onOpenChange(false);
          void navigate({
            to: "/statement-batches/$kind/$statementBatchId",
            params: {
              kind: kind.toLowerCase(),
              statementBatchId: data.createStatementBatch.statementBatch.id,
            },
          });
        },
        onError: (error) => {
          const description =
            error.graphQLErrors?.[0]?.message ?? defaultErrorMessage;
          toast({
            kind: "error",
            autoHide: false,
            title: "Failed to generate statements",
            description,
          });
          throw error;
        },
      });
    },
  });

  const periodicSelectedOrganisationOptions = useStore(
    periodicForm.store,
    (state) => state.values.selectedOrganisationOptions,
  );
  const periodicStartAtInclusive = useStore(
    periodicForm.store,
    (state) => state.values.startAtInclusive,
  );
  const periodicEndAtExclusive = useStore(
    periodicForm.store,
    (state) => state.values.endAtExclusive,
  );
  const balanceSelectedOrganisationOptions = useStore(
    balanceForm.store,
    (state) => state.values.selectedOrganisationOptions,
  );
  const selectedOrganisationOptions =
    kind === "BALANCE"
      ? balanceSelectedOrganisationOptions
      : periodicSelectedOrganisationOptions;
  const allOrganisations = selectedOrganisationOptions === undefined;

  const statementKindName = prettyPrintEnum(kind).toLowerCase();

  const portalRef = useRef<HTMLDivElement | null>(null);

  const organisationSelectionBlock = (
    <>
      <h2 className="font-semibold text-sm">Organisations</h2>
      <div className="flex flex-row gap-2">
        <input
          type="checkbox"
          id="allOrgs"
          checked={allOrganisations}
          onChange={(e) => {
            const nextValue = e.target.checked ? undefined : [];
            if (kind === "BALANCE") {
              balanceForm.setFieldValue(
                "selectedOrganisationOptions",
                nextValue,
              );
              return;
            }
            periodicForm.setFieldValue(
              "selectedOrganisationOptions",
              nextValue,
            );
          }}
        />
        <label className="text-sm" htmlFor="allOrgs">
          All organisations
        </label>
      </div>
      {allOrganisations ? (
        <div className="text-tertiary text-xs">
          A {statementKindName} statement will be generated for all active
          organisations.
        </div>
      ) : (
        <>
          <div className="text-tertiary text-xs">
            Select the organisations that should be included in this{" "}
            {statementKindName} statement batch.
          </div>
          {kind === "BALANCE" ? (
            <balanceForm.AppField name="selectedOrganisationOptions">
              {(field) => (
                <>
                  <Select
                    options={organisationOptions}
                    isMulti
                    value={field.state.value}
                    onChange={(selected) => field.handleChange([...selected])}
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 60 }),
                    }}
                    placeholder="Select or type to add..."
                    noOptionsMessage={() => "No matching organisations found."}
                  />
                  <div className="text-danger text-xs">
                    {!field.state.meta.isValid &&
                      field.state.meta.errors.map((v) => v?.message).join(", ")}
                  </div>
                </>
              )}
            </balanceForm.AppField>
          ) : (
            <periodicForm.AppField name="selectedOrganisationOptions">
              {(field) => (
                <div ref={portalRef}>
                  <Select
                    options={organisationOptions}
                    isMulti
                    value={selectedOrganisationOptions}
                    onChange={(selected) => field.handleChange([...selected])}
                    isClearable
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    menuPortalTarget={portalRef.current}
                    menuPosition="fixed"
                    isSearchable
                    placeholder="Select or type to add..."
                    noOptionsMessage={() => "No matching organisations found."}
                  />
                  <div className="text-danger text-xs">
                    {!field.state.meta.isValid &&
                      field.state.meta.errors.map((v) => v?.message).join(", ")}
                  </div>
                </div>
              )}
            </periodicForm.AppField>
          )}
        </>
      )}
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{`Generate ${statementKindName} statements`}</DialogTitle>
        </DialogHeader>

        {kind === "BALANCE" ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              balanceForm.handleSubmit();
            }}
          >
            <balanceForm.AppForm>
              <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-2">
                  <balanceForm.AppField name="atExclusive">
                    {(field) => (
                      <div className="flex flex-col gap-2 rounded-lg border p-8">
                        <label
                          className="font-semibold text-sm"
                          htmlFor={field.name}
                        >
                          Date
                        </label>
                        <div className="flex items-center gap-1 text-sm">
                          <input
                            className="border p-1"
                            type="datetime-local"
                            id={field.name}
                            name={field.name}
                            value={DateTime.fromJSDate(field.state.value, {
                              zone: userTimezone,
                            }).toFormat(browserDateTimeFormat)}
                            onChange={(e) => {
                              const dt = DateTime.fromFormat(
                                e.target.value,
                                browserDateTimeFormat,
                                { zone: userTimezone },
                              );
                              if (!dt.isValid) {
                                return;
                              }
                              field.handleChange(dt.toJSDate());
                            }}
                          />
                          <div>in {userTimezone} (user timezone)</div>
                        </div>
                        <div className="text-tertiary text-xs">
                          {DateTime.fromJSDate(field.state.value)
                            .setZone(REPORT_TIMEZONE)
                            .toISO()}{" "}
                          in {REPORT_TIMEZONE} (report timezone)
                        </div>
                        <div className="text-tertiary text-xs">
                          Exclusive. Will not include transactions which occured
                          exactly at or after this date and time.
                        </div>
                        <div className="text-danger text-xs">
                          {!field.state.meta.isValid &&
                            field.state.meta.errors
                              .map((v) => v?.message)
                              .join(", ")}
                        </div>
                      </div>
                    )}
                  </balanceForm.AppField>

                  <div className="flex flex-col gap-2 rounded-lg border p-8">
                    {organisationSelectionBlock}
                  </div>
                </div>

                <div className="flex w-full flex-row justify-end">
                  <balanceForm.SubmitButton>
                    Generate statements
                  </balanceForm.SubmitButton>
                </div>
              </div>
            </balanceForm.AppForm>
          </form>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              periodicForm.handleSubmit();
            }}
          >
            <periodicForm.AppForm>
              <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-2">
                  <div className="flex flex-row gap-12 rounded-lg border p-8">
                    <periodicForm.AppField name="startAtInclusive">
                      {(field) => (
                        <div className="flex flex-col gap-1">
                          <label
                            className="font-semibold text-sm"
                            htmlFor={field.name}
                          >
                            Start at
                          </label>
                          <div className="flex items-center gap-1 text-sm">
                            <input
                              className="border p-1"
                              type="datetime-local"
                              id={field.name}
                              name={field.name}
                              max={DateTime.fromJSDate(periodicEndAtExclusive, {
                                zone: userTimezone,
                              }).toFormat(browserDateTimeFormat)}
                              value={DateTime.fromJSDate(field.state.value, {
                                zone: userTimezone,
                              }).toFormat(browserDateTimeFormat)}
                              onChange={(e) => {
                                const dt = DateTime.fromFormat(
                                  e.target.value,
                                  browserDateTimeFormat,
                                  { zone: userTimezone },
                                );
                                if (!dt.isValid) {
                                  return;
                                }
                                field.handleChange(dt.toJSDate());
                              }}
                            />
                            <div>in {userTimezone} (user timezone)</div>
                          </div>
                          <div className="text-tertiary text-xs">
                            {DateTime.fromJSDate(field.state.value)
                              .setZone(REPORT_TIMEZONE)
                              .toISO()}{" "}
                            in {REPORT_TIMEZONE} (report timezone)
                          </div>
                          <div className="text-tertiary text-xs">
                            Inclusive. Will include transactions which occured
                            exactly at this date and time, but not before.
                          </div>
                          <div className="text-danger text-xs">
                            {field.state.meta.errors
                              ?.map((v) => v?.message)
                              .join(", ")}
                          </div>
                        </div>
                      )}
                    </periodicForm.AppField>

                    <periodicForm.AppField name="endAtExclusive">
                      {(field) => (
                        <div className="flex flex-col gap-1">
                          <label
                            className="font-semibold text-sm"
                            htmlFor={field.name}
                          >
                            End at
                          </label>
                          <div className="flex items-center gap-1 text-sm">
                            <input
                              className="border p-1"
                              type="datetime-local"
                              id={field.name}
                              name={field.name}
                              min={DateTime.fromJSDate(
                                periodicStartAtInclusive,
                                {
                                  zone: userTimezone,
                                },
                              ).toFormat(browserDateTimeFormat)}
                              value={DateTime.fromJSDate(field.state.value, {
                                zone: userTimezone,
                              }).toFormat(browserDateTimeFormat)}
                              onChange={(e) => {
                                const dt = DateTime.fromFormat(
                                  e.target.value,
                                  browserDateTimeFormat,
                                  { zone: userTimezone },
                                );
                                if (!dt.isValid) {
                                  return;
                                }
                                field.handleChange(dt.toJSDate());
                              }}
                            />
                            <div>in {userTimezone} (user timezone)</div>
                          </div>
                          <div className="text-tertiary text-xs">
                            {DateTime.fromJSDate(field.state.value)
                              .setZone(REPORT_TIMEZONE)
                              .toISO()}{" "}
                            in {REPORT_TIMEZONE} (report timezone)
                          </div>
                          <div className="text-tertiary text-xs">
                            Exclusive. Will not include transactions which
                            occured exactly at or after this date and time.
                          </div>
                          <div className="text-danger text-xs">
                            {field.state.meta.errors
                              ?.map((v) => v?.message)
                              .join(", ")}
                          </div>
                        </div>
                      )}
                    </periodicForm.AppField>
                  </div>

                  <div className="flex flex-col gap-2 rounded-lg border p-8">
                    {organisationSelectionBlock}
                  </div>
                </div>

                <div className="flex w-full flex-row justify-end">
                  <periodicForm.SubmitButton>
                    Generate statements
                  </periodicForm.SubmitButton>
                </div>
              </div>
            </periodicForm.AppForm>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function View() {
  const navigate = useNavigate();
  const params = Route.useParams();
  const [isGenerateDialogOpen, setGenerateDialogOpen] = useState(false);

  const kind = statementQueryParamToKind(params.kind);
  if (!kind) {
    throw new NotFoundError();
  }

  const baseInput = {
    filter: {
      kinds: [kind],
    },
    first: 20,
  };

  const {
    fetchMore,
    loading,
    networkStatus,
    data: tableData,
  } = useQuery(
    graphql(`
      query AdminStatementBatches($after: String, $first: Int!, $filter: StatementBatchFilter!) {
        statementBatches(after: $after, first: $first, filter: $filter) {
          pageInfo {
            endCursor
            hasNextPage
          }
          statementBatches {
            ...AdminStatementBatchRow
          }
        }
      }

      fragment AdminStatementBatchRow on StatementBatch {
        id
        createdAt
        shortRef
        periodStartAtInclusive
        periodEndAtExclusive
        totalFeeExGst {
          currency
          amount
        }
        statements {
          id
          status
        }
      }
    `),
    {
      variables: {
        after: null,
        ...baseInput,
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminStatementBatchesPage {
        organisations {
          id
          name
        }
      }
    `),
  );

  const statementBatches = tableData?.statementBatches?.statementBatches ?? [];
  const organisations = data?.organisations;

  const nextCursor = tableData?.statementBatches?.pageInfo.hasNextPage
    ? (tableData.statementBatches.pageInfo.endCursor ?? undefined)
    : undefined;

  return (
    <AdminPageContainer testId={`admin-${kind}-statements`}>
      <AdminPageHeader
        title="Statements"
        className="pb-12"
        subtitle={
          <Tabs
            value={params.kind}
            onValueChange={(value) =>
              navigate({
                to: "/statement-batches/$kind",
                params: { kind: value },
              })
            }
          >
            <TabsList size="sm" className="mt-4">
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
              <TabsTrigger value="balance">Balance</TabsTrigger>
              <TabsTrigger value="account">Account</TabsTrigger>
            </TabsList>
          </Tabs>
        }
      />
      <div className="mb-8 flex flex-col gap-8">
        <div className="flex flex-row gap-4">
          <Button variant="primary" onClick={() => setGenerateDialogOpen(true)}>
            Generate statements
          </Button>
        </div>
      </div>
      <div>
        <DataTable
          data={statementBatches}
          columns={getColumns(kind)}
          isLoadingData={loading || networkStatus === NetworkStatus.fetchMore}
          manualPagination={true}
          maxMultiSortColCount={1}
          hidePagination={true}
          manualSorting={true}
          pageSize={10_000}
          onRowClick={(row) => {
            void navigate({
              to: "/statement-batches/$kind/$statementBatchId",
              params: {
                kind: params.kind,
                statementBatchId: row.original.id,
              },
            });
          }}
          className="overflow-hidden border"
        />
        <div className="mt-4 flex w-full flex-row justify-center">
          {nextCursor ? (
            <Button
              onClick={() =>
                fetchMore({ variables: { ...baseInput, after: nextCursor } })
              }
              variant="outline"
              loading={loading || networkStatus === NetworkStatus.fetchMore}
            >
              Load more
            </Button>
          ) : (
            !!statementBatches.length && (
              <p className="text-secondary text-sm">No more data</p>
            )
          )}
        </div>
      </div>

      <GenerateStatementBatchDialog
        kind={kind}
        open={isGenerateDialogOpen}
        onOpenChange={setGenerateDialogOpen}
        organisations={organisations ?? []}
      />
    </AdminPageContainer>
  );
}

function statementQueryParamToKind(s?: string): StatementKind | null {
  switch (s) {
    case "monthly":
      return "MONTHLY";
    case "activity":
      return "ACTIVITY";
    case "balance":
      return "BALANCE";
    case "account":
      return "ACCOUNT";
    default:
      return null;
  }
}
